import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { PasswordCaptureFields } from '../types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { ContentTab } from './tabs/ContentTab';
import { LayoutTab } from './tabs/LayoutTab';
import { FieldsTab } from './tabs/FieldsTab';
import { StyleTab } from './tabs/StyleTab';
import { PasswordCapturePreview } from './PasswordCapturePreview';
import { useToast } from '@/hooks/use-toast';
import { Save } from 'lucide-react';
import { videoService } from '@/services/videoService';
import { vsleadsService } from '@/services/vsleadsService';
import { useNavigate } from 'react-router-dom';

interface PasswordCaptureEditorProps {
  isEditMode?: boolean;
  videoId?: string;
}

const getDefaultPasswordCaptureConfig = (): PasswordCaptureFields => ({
  videoId: '',
  tipo: 'PASSWORD_GATE',
  is_active: true,
  layout: {
    position: 'center',
    width: 420,
    padding: 24,
    offset: { top: 0, right: 0, bottom: 0, left: 0 },
    overlay_opacity: 0.3,
  },
  content: {
    title: {
      text: 'Acesse o vídeo',
      position: 'inside',
    },
    password: {
      value: '',
      placeholder: 'Insira a senha para acessar o vídeo',
      error_message: 'Senha incorreta. Tente novamente.',
      case_sensitive: false,
      max_attempts: 0,
      lockout_minutes: 15,
    },
    button_label: 'Acessar vídeo',
    footer: '',
  },
  style: {
    container: {
      bg_color: '#0B1020',
      bg_opacity: 0.9,
      border_radius: 12,
      shadow: 2,
      glass_blur: 0,
      border: { width: 0, color: '#E5E7EB' },
    },
    input: {
      bg_color: '#FFFFFF',
      text_color: '#111111',
      border_color: '#E7E9F1',
      border_radius: 8,
    },
    button: {
      bg_color: '#2563EB',
      text_color: '#FFFFFF',
      hover_color: '#1E50BF',
      pressed_color: '#194399',
      border_radius: 8,
    },
    text: {
      title_color: '#FFFFFF',
      body_color: '#94A3B8',
    },
  },
  timer: {
    enabled: false,
    showAt: '00:15',
    pauseOnOpen: true,
    pauseOnSubmit: false,
  },
});

export function PasswordCaptureEditor({ isEditMode = false, videoId }: PasswordCaptureEditorProps) {
  useLanguage();
  const [activeTab, setActiveTab] = useState('content');
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const form = useForm<PasswordCaptureFields>({
    defaultValues: getDefaultPasswordCaptureConfig(),
  });

  const config = form.watch();
  const watchedVideoId = form.watch('videoId');

  // Buscar URL do vídeo quando videoId mudar
  useEffect(() => {
    const loadVideoUrl = async () => {
      const targetVideoId = watchedVideoId || videoId;
      if (!targetVideoId) {
        setVideoUrl(null);
        return;
      }

      try {
        const response = await videoService.getVideoById(targetVideoId);

        // Extrair dados do vídeo
        let videoData = null;
        if (response?.response) {
          if (Array.isArray(response.response)) {
            videoData = response.response[0];
          } else {
            videoData = response.response;
          }
        } else if (Array.isArray(response)) {
          videoData = response[0];
        } else {
          videoData = response?.data || response;
        }

        // Priorizar videoMP4 que é o campo correto
        const foundVideoUrl = videoData?.videoMP4 || videoData?.videoDownload || videoData?.video_download || videoData?.videoUrl || videoData?.video;

        if (foundVideoUrl) {
          setVideoUrl(foundVideoUrl);
        }
      } catch (error) {
        console.error('Erro ao carregar vídeo:', error);
      }
    };

    loadVideoUrl();
  }, [watchedVideoId, videoId]);

  // Carregar dados do vídeo quando em modo de edição
  useEffect(() => {
    const loadVideoData = async () => {
      if (!videoId || !isEditMode) {
        return;
      }

      setIsLoading(true);
      try {
        const response = await videoService.getVideoById(videoId);

        // Extrair dados do vídeo
        let videoData = null;
        if (response?.response) {
          if (Array.isArray(response.response)) {
            videoData = response.response[0];
          } else {
            videoData = response.response;
          }
        } else if (Array.isArray(response)) {
          videoData = response[0];
        } else {
          videoData = response?.data || response;
        }

        if (videoData && videoData.typeForm === 'capture-password') {
          // Converter dados do backend para o formato do formulário
          const formData: Partial<PasswordCaptureFields> = {
            videoId: videoData.id_video || videoId,
            is_active: videoData.haveForm || false,
          };

          // Content
          formData.content = {
            title: {
              text: videoData.captureTitle || 'Acesse o vídeo',
              position: 'inside',
            },
            password: {
              value: videoData.password || '',
              placeholder: 'Insira a senha para acessar o vídeo',
              error_message: 'Senha incorreta. Tente novamente.',
              case_sensitive: false,
              max_attempts: 0,
              lockout_minutes: 15,
            },
            button_label: videoData.captureButtonText || 'Acessar vídeo',
            footer: videoData.captureAuxText || '',
          };

          // Style
          const defaultConfig = getDefaultPasswordCaptureConfig();
          formData.style = {
            container: {
              bg_color: videoData.captureBackgroundColor || defaultConfig.style.container.bg_color,
              bg_opacity: defaultConfig.style.container.bg_opacity,
              border_radius: defaultConfig.style.container.border_radius,
              shadow: defaultConfig.style.container.shadow,
              glass_blur: defaultConfig.style.container.glass_blur,
              border: defaultConfig.style.container.border,
            },
            input: {
              bg_color: videoData.captureInputColor || defaultConfig.style.input.bg_color,
              text_color: defaultConfig.style.input.text_color,
              border_color: defaultConfig.style.input.border_color,
              border_radius: defaultConfig.style.input.border_radius,
            },
            button: {
              bg_color: videoData.captureButtonColor || defaultConfig.style.button.bg_color,
              text_color: videoData.captureButtonTextColor || defaultConfig.style.button.text_color,
              hover_color: defaultConfig.style.button.hover_color,
              pressed_color: defaultConfig.style.button.pressed_color,
              border_radius: defaultConfig.style.button.border_radius,
            },
            text: {
              title_color: videoData.captureTitleColor || defaultConfig.style.text.title_color,
              body_color: videoData.captureAuxTextColor || defaultConfig.style.text.body_color,
            },
          };

          // Layout
          formData.layout = {
            position: 'center',
            width: 420,
            padding: 24,
            offset: { top: 0, right: 0, bottom: 0, left: 0 },
            overlay_opacity: videoData.haveBackgroundColored ? 0.7 : 0,
          };

          // Timer
          formData.timer = {
            enabled: false,
            showAt: '00:15',
            pauseOnOpen: true,
            pauseOnSubmit: false,
          };

          // Resetar form com novos valores
          form.reset({ ...getDefaultPasswordCaptureConfig(), ...formData });

          console.log('Dados carregados do vídeo:', formData);
        }
      } catch (error) {
        console.error('Erro ao carregar dados do vídeo:', error);
        toast({
          title: 'Erro ao carregar',
          description: 'Não foi possível carregar os dados do vídeo.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadVideoData();
  }, [videoId, isEditMode]);

  const handleSave = async (data: PasswordCaptureFields) => {
    const targetVideoId = data.videoId || videoId;

    if (!targetVideoId) {
      toast({
        title: translateInterfaceText("Erro"),
        description: translateInterfaceText("É necessário selecionar um vídeo."),
        variant: 'destructive',
      });
      return;
    }

    if (!data.content.password.value) {
      toast({
        title: translateInterfaceText("Erro"),
        description: translateInterfaceText("É necessário definir uma senha."),
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      // Converter dados do formulário para formato do backend
      const backendData = {
        // Ativação do recurso
        haveForm: data.is_active,
        typeForm: 'capture-password' as const,

        // Senha (backend espera 'password', não 'senha')
        password: data.content.password.value,

        // Textos
        captureTitle: data.content.title.text,
        captureButtonText: data.content.button_label,
        captureAuxText: '',
        haveCaptureAuxText: false,

        // Cores
        captureTitleColor: data.style.text.title_color,
        captureAuxTextColor: data.style.text.body_color,
        captureButtonColor: data.style.button.bg_color,
        captureButtonTextColor: data.style.button.text_color,
        captureBackgroundColor: data.style.container.bg_color,

        // Timer (não usado em senha)
        checkedCaptureTimer: false,
        haveTime: 0,

        // Layout
        checkedCaptureVertical: false,
        haveBackgroundColored: data.style.container.bg_color.includes('rgba'),

        // Campos não usados em senha
        haveInputName: false,
        haveInputEmail: false,
        haveInputWhatsapp: false,
        skipLead: false,
        haveCaptureBigTitle: false,
        haveCaptureText: false,
      };

      console.log('Salvando configuração de senha:', backendData);

      // Salvar configuração de senha
      await vsleadsService.updatePasswordCapture(targetVideoId, backendData);

      toast({
        title: translateInterfaceText("Configuração salva!"),
        description: data.is_active
          ? translateInterfaceText("A captura por senha foi configurada com sucesso.")
          : translateInterfaceText("A captura por senha foi desativada com sucesso."),
      });

      navigate('/vsleads?tab=captura-senha');
    } catch (error) {
      console.error('Erro ao salvar configuração:', error);
      toast({
        title: translateInterfaceText("Erro ao salvar"),
        description: translateInterfaceText("Não foi possível salvar as configurações."),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        {/* ESTRUTURA PRINCIPAL: 2 COLUNAS */}
        <div className="flex flex-col lg:flex-row gap-8">

        {/* COLUNA ESQUERDA: Editor + Tabs */}
        <div className="flex-1 lg:max-w-[780px] pr-2">
          <div className="space-y-6 pb-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              {/* Tabs alinhadas à esquerda */}
              <TabsList className="flex flex-wrap gap-3 mb-8">
                <TabsTrigger value="content">{translateInterfaceText("Conteúdo")}</TabsTrigger>
                <TabsTrigger value="layout">{translateInterfaceText("Layout")}</TabsTrigger>
                <TabsTrigger value="fields">{translateInterfaceText("Campos")}</TabsTrigger>
                <TabsTrigger value="style">{translateInterfaceText("Estilo")}</TabsTrigger>
              </TabsList>

              {/* Conteúdo das tabs */}
              <div className="mt-8 space-y-8">
                <TabsContent value="content" className="mt-0 space-y-8">
                  <ContentTab form={form} isEditMode={isEditMode} />
                </TabsContent>

                <TabsContent value="layout" className="mt-0 space-y-8">
                  <LayoutTab form={form} />
                </TabsContent>

                <TabsContent value="fields" className="mt-0 space-y-8">
                  <FieldsTab form={form} />
                </TabsContent>

                <TabsContent value="style" className="mt-0 space-y-8">
                  <StyleTab form={form} />
                </TabsContent>
              </div>
            </Tabs>

            {/* BOTÕES DE AÇÃO */}
            <div className="flex gap-3 justify-end pb-6">
              <Button
                type="submit"
                disabled={isLoading}
                className="gap-2 h-11 px-6 rounded-[12px] bg-gradient-to-b from-[#2D72FF] to-[#1B52CC] shadow-[0_3px_6px_rgba(45,114,255,0.25)] hover:brightness-110 hover:-translate-y-0.5 transition-all duration-200"
              >
                <Save className="h-4 w-4" />
                {isLoading ? translateInterfaceText("Salvando...") : translateInterfaceText("Salvar Configuração")}
              </Button>
            </div>
          </div>
        </div>

        {/* COLUNA DIREITA: Preview Sticky (flexível, ocupa o resto) */}
        <div className="flex-1 lg:min-w-[520px]">
          <div className="lg:sticky lg:top-6">
            <PasswordCapturePreview config={config} videoUrl={videoUrl} isLoading={isLoading} />
          </div>
        </div>
      </div>
    </form>
    </FormProvider>
  );
}
